<template>
  <div class="tab-bar">
    <div class="tab-bar__scroll">
      <div
        v-for="tab in tabsStore.tabs"
        :key="tab.path"
        :class="['tab-item', { 'is-active': tab.path === tabsStore.activeTabPath }]"
        @click="handleTabClick(tab)"
        @contextmenu.prevent="handleContextMenu($event, tab)"
      >
        <el-icon v-if="tab.icon" class="tab-icon">
          <component :is="tab.icon" />
        </el-icon>
        <span class="tab-title">{{ tab.title }}</span>
        <el-icon
          v-if="tab.closable"
          class="tab-close"
          @click.stop="handleTabClose(tab)"
        >
          <Close />
        </el-icon>
      </div>
    </div>

    <!-- 右键菜单 -->
    <TabContextMenu
      v-if="contextMenuVisible"
      :visible="contextMenuVisible"
      :x="contextMenuX"
      :y="contextMenuY"
      :tab="contextMenuTab"
      @close="contextMenuVisible = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { Close } from '@element-plus/icons-vue'
import { useTabsStore } from '@/stores/tabs'
import type { TabItem } from '@/stores/tabs'
import TabContextMenu from './TabContextMenu.vue'

const router = useRouter()
const tabsStore = useTabsStore()

// 右键菜单状态
const contextMenuVisible = ref(false)
const contextMenuX = ref(0)
const contextMenuY = ref(0)
const contextMenuTab = ref<TabItem | null>(null)

/**
 * 点击标签页
 */
const handleTabClick = (tab: TabItem) => {
  if (tab.path !== tabsStore.activeTabPath) {
    router.push(tab.fullPath)
  }
}

/**
 * 关闭标签页
 */
const handleTabClose = (tab: TabItem) => {
  const targetPath = tabsStore.removeTab(tab.path)
  if (targetPath) {
    router.push(targetPath)
  }
}

/**
 * 右键菜单
 */
const handleContextMenu = (event: MouseEvent, tab: TabItem) => {
  contextMenuX.value = event.clientX
  contextMenuY.value = event.clientY
  contextMenuTab.value = tab
  contextMenuVisible.value = true
}
</script>

<style lang="scss" scoped>
.tab-bar {
  height: 34px;
  background-color: #fff;
  border-bottom: 1px solid #e4e7ed;
  position: relative;

  &__scroll {
    height: 100%;
    display: flex;
    align-items: center;
    padding: 0 8px;
    overflow-x: auto;
    overflow-y: hidden;
    white-space: nowrap;

    &::-webkit-scrollbar {
      height: 3px;
    }

    &::-webkit-scrollbar-thumb {
      background-color: #dcdfe6;
      border-radius: 2px;
    }
  }

  .tab-item {
    display: inline-flex;
    align-items: center;
    height: 26px;
    padding: 0 10px;
    margin-right: 4px;
    background-color: #f5f7fa;
    border: 1px solid #e4e7ed;
    border-radius: 3px;
    cursor: pointer;
    transition: all 0.3s;
    user-select: none;
    flex-shrink: 0;

    &:hover {
      background-color: #ecf5ff;
      border-color: #b3d8ff;

      .tab-close {
        opacity: 1;
      }
    }

    &.is-active {
      background-color: #409eff;
      border-color: #409eff;
      color: #fff;

      .tab-icon,
      .tab-title {
        color: #fff;
      }

      .tab-close {
        opacity: 1;
        color: #fff;

        &:hover {
          background-color: rgba(255, 255, 255, 0.2);
        }
      }
    }

    .tab-icon {
      font-size: 12px;
      margin-right: 4px;
      color: #606266;
    }

    .tab-title {
      font-size: 12px;
      color: #606266;
      max-width: 100px;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .tab-close {
      font-size: 12px;
      margin-left: 4px;
      color: #909399;
      opacity: 0;
      transition: all 0.2s;
      border-radius: 50%;
      padding: 1px;

      &:hover {
        background-color: rgba(0, 0, 0, 0.1);
        color: #606266;
      }
    }
  }
}
</style>
